import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { Customer } from '../Models/customer.model';
import {
  Auftrag,
  AuftragAssignment,
  AuftragAssignmentCreate,
  AuftragCreate,
  AuftragOverview,
  CustomerRef,
  EinsatzMaterial,
  PersonHours,
  PlannableUser,
  SpontanEinsatzCreate,
  StammkundeMember,
  StammkundeWorker,
  TakedaAbrechnung,
  TakedaExportEinsatzOption,
  TakedaLeistungsverzeichnis,
  TakedaLvPosition,
  TakedaPricing,
  TakedaProjektDaten,
  TakedaLvPositionSave,
  TakedaTagesbericht,
  TakedaTagesberichtLvPosition,
  TakedaTagesberichtSave,
  WarehouseRef,
  ZeitEintrag,
  ZeitEintragCreate,
  TakedaEinsatzort,
  TakedaEinsatzortSave,
  TakedaEinsatzortVorschlag,
  TakedaProjektnummer,
  TakedaProjektnummerSave
} from '../Models/stammkunde.model';

/**
 * API-Zugriff für den Stammkunden-Bereich: Aufträge, Einsatzplanung je Auftrag,
 * Team-Mitgliedschaften und Lager-Verknüpfung.
 */
@Injectable({ providedIn: 'root' })
export class StammkundeService {
  private customerUrl = environment.apiBaseUrl + '/customer';
  private auftragUrl = environment.apiBaseUrl + '/auftrag';
  private accessUrl = environment.apiBaseUrl + '/access';

  constructor(private http: HttpClient) { }

  // ── Stammkunden (Kunden mit Type=Stammkunde) ──

  getStammkunden(): Observable<Customer[]> {
    return this.http.get<Customer[]>(`${this.customerUrl}?type=Stammkunde`);
  }

  // ── Aufträge ──

  getAuftraege(customerId?: number): Observable<Auftrag[]> {
    const query = customerId != null ? `?customerId=${customerId}` : '';
    return this.http.get<Auftrag[]>(`${this.auftragUrl}${query}`);
  }

  getAuftrag(id: number): Observable<Auftrag> {
    return this.http.get<Auftrag>(`${this.auftragUrl}/${id}`);
  }

  createAuftrag(auftrag: AuftragCreate): Observable<Auftrag> {
    return this.http.post<Auftrag>(this.auftragUrl, auftrag);
  }

  updateAuftrag(id: number, auftrag: AuftragCreate): Observable<Auftrag> {
    return this.http.put<Auftrag>(`${this.auftragUrl}/${id}`, auftrag);
  }

  deleteAuftrag(id: number): Observable<void> {
    return this.http.delete<void>(`${this.auftragUrl}/${id}`);
  }

  // ── Einsatzplanung je Auftrag ──

  getAssignments(auftragId: number): Observable<AuftragAssignment[]> {
    return this.http.get<AuftragAssignment[]>(`${this.auftragUrl}/${auftragId}/assignments`);
  }

  createAssignment(auftragId: number, assignment: AuftragAssignmentCreate): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(`${this.auftragUrl}/${auftragId}/assignments`, assignment);
  }

  updateAssignment(auftragId: number, assignmentId: number, assignment: AuftragAssignmentCreate): Observable<AuftragAssignment> {
    return this.http.put<AuftragAssignment>(`${this.auftragUrl}/${auftragId}/assignments/${assignmentId}`, assignment);
  }

  deleteAssignment(auftragId: number, assignmentId: number, sendCancellation = false): Observable<void> {
    return this.http.delete<void>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}?sendCancellation=${sendCancellation}`);
  }

  getPlannableUsers(): Observable<PlannableUser[]> {
    return this.http.get<PlannableUser[]>(`${this.auftragUrl}/plannable-users`);
  }

  /** Alle Einsätze eines Stammkunden über alle Aufträge (für das Planungsboard). */
  getCustomerAssignments(customerId: number, from: string, to: string): Observable<AuftragAssignment[]> {
    return this.http.get<AuftragAssignment[]>(
      `${this.auftragUrl}/customer/${customerId}/assignments?from=${from}&to=${to}`);
  }

  // ── Meine Einsätze (Vorarbeiter-Sicht) ──

  /** Eigene Einsätze über alle Stammkunden-Aufträge, optional auf einen Zeitraum. */
  getMyAssignments(from?: string, to?: string): Observable<AuftragAssignment[]> {
    const params: string[] = [];
    if (from) { params.push(`from=${from}`); }
    if (to) { params.push(`to=${to}`); }
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<AuftragAssignment[]>(`${this.auftragUrl}/my-assignments${query}`);
  }

  /** Arbeitsstatus eines Einsatzes setzen (Bestätigt/Unterwegs/Erledigt). */
  setAssignmentWorkStatus(auftragId: number, assignmentId: number, status: string): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/work-status`, { status });
  }

  /** Stammkunden, für die der Benutzer einen Spontan-Einsatz anlegen darf. */
  getMyCustomers(): Observable<CustomerRef[]> {
    return this.http.get<CustomerRef[]>(`${this.auftragUrl}/my-customers`);
  }

  /** Spontan-Einsatz anlegen (Vorarbeiter teilt sich selbst ein). */
  createSpontanEinsatz(einsatz: SpontanEinsatzCreate): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(`${this.auftragUrl}/spontan`, einsatz);
  }

  /** Material eines Einsatzes vollständig ersetzen (Vorarbeiter dokumentiert Verbrauch). */
  setAssignmentMaterials(auftragId: number, assignmentId: number, materials: EinsatzMaterial[]): Observable<AuftragAssignment> {
    return this.http.put<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/materials`, materials);
  }

  // ── Stunden-Erfassung (ohne Preise) ──

  getTimeEntries(auftragId: number): Observable<ZeitEintrag[]> {
    return this.http.get<ZeitEintrag[]>(`${this.auftragUrl}/${auftragId}/time-entries`);
  }

  addTimeEntry(auftragId: number, entry: ZeitEintragCreate): Observable<ZeitEintrag> {
    return this.http.post<ZeitEintrag>(`${this.auftragUrl}/${auftragId}/time-entries`, entry);
  }

  updateTimeEntry(auftragId: number, entryId: number, entry: ZeitEintragCreate): Observable<ZeitEintrag> {
    return this.http.put<ZeitEintrag>(`${this.auftragUrl}/${auftragId}/time-entries/${entryId}`, entry);
  }

  deleteTimeEntry(auftragId: number, entryId: number): Observable<void> {
    return this.http.delete<void>(`${this.auftragUrl}/${auftragId}/time-entries/${entryId}`);
  }

  approveTimeEntry(auftragId: number, entryId: number): Observable<ZeitEintrag> {
    return this.http.post<ZeitEintrag>(`${this.auftragUrl}/${auftragId}/time-entries/${entryId}/approve`, {});
  }

  reopenTimeEntry(auftragId: number, entryId: number): Observable<ZeitEintrag> {
    return this.http.post<ZeitEintrag>(`${this.auftragUrl}/${auftragId}/time-entries/${entryId}/reopen`, {});
  }

  /** Alle offenen Stunden eines Auftrags auf einmal freigeben. */
  approveAllTimeEntries(auftragId: number): Observable<{ approved: number }> {
    return this.http.post<{ approved: number }>(`${this.auftragUrl}/${auftragId}/time-entries/approve-all`, {});
  }


  // ── Takeda-Stammdaten: Einsatzorte und Projektnummern ──
  //
  // Gleiche Route und Rechteprüfung wie das Leistungsverzeichnis:
  // api/stammkunde/{customerId}/takeda/...

  private stammdatenUrl(customerId: number): string {
    return `${environment.apiBaseUrl}/stammkunde/${customerId}/takeda`;
  }

  /** @param nurAktive true für Auswahlfelder; false lädt auch Deaktivierte (Pflege). */
  getTakedaEinsatzorte(customerId: number, nurAktive = false): Observable<TakedaEinsatzort[]> {
    const params = new HttpParams().set('nurAktive', nurAktive);
    return this.http.get<TakedaEinsatzort[]>(
      `${this.stammdatenUrl(customerId)}/einsatzorte`, { params });
  }

  createTakedaEinsatzort(customerId: number, ort: TakedaEinsatzortSave): Observable<TakedaEinsatzort> {
    return this.http.post<TakedaEinsatzort>(
      `${this.stammdatenUrl(customerId)}/einsatzorte`, ort);
  }

  updateTakedaEinsatzort(
    customerId: number, id: number, ort: TakedaEinsatzortSave
  ): Observable<TakedaEinsatzort> {
    return this.http.put<TakedaEinsatzort>(
      `${this.stammdatenUrl(customerId)}/einsatzorte/${id}`, ort);
  }

  deleteTakedaEinsatzort(customerId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${this.stammdatenUrl(customerId)}/einsatzorte/${id}`);
  }

  /** Vorhandene Equipment-Namen als Übernahmevorschlag (nur lesend). */
  getTakedaEinsatzortVorschlaege(customerId: number): Observable<TakedaEinsatzortVorschlag[]> {
    return this.http.get<TakedaEinsatzortVorschlag[]>(
      `${this.stammdatenUrl(customerId)}/einsatzorte/vorschlaege`);
  }

  /** Legt die gewählten Vorschläge an; vorhandene Namen werden übersprungen. */
  uebernehmeTakedaEinsatzorte(customerId: number, equipmentIds: number[]): Observable<TakedaEinsatzort[]> {
    return this.http.post<TakedaEinsatzort[]>(
      `${this.stammdatenUrl(customerId)}/einsatzorte/uebernehmen`, { equipmentIds });
  }

  getTakedaProjektnummern(customerId: number, nurAktive = false): Observable<TakedaProjektnummer[]> {
    const params = new HttpParams().set('nurAktive', nurAktive);
    return this.http.get<TakedaProjektnummer[]>(
      `${this.stammdatenUrl(customerId)}/projektnummern`, { params });
  }

  createTakedaProjektnummer(
    customerId: number, eintrag: TakedaProjektnummerSave
  ): Observable<TakedaProjektnummer> {
    return this.http.post<TakedaProjektnummer>(
      `${this.stammdatenUrl(customerId)}/projektnummern`, eintrag);
  }

  updateTakedaProjektnummer(
    customerId: number, id: number, eintrag: TakedaProjektnummerSave
  ): Observable<TakedaProjektnummer> {
    return this.http.put<TakedaProjektnummer>(
      `${this.stammdatenUrl(customerId)}/projektnummern/${id}`, eintrag);
  }

  deleteTakedaProjektnummer(customerId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${this.stammdatenUrl(customerId)}/projektnummern/${id}`);
  }

  // ── Takeda-Leistungsverzeichnis ──

  getTakedaLeistungsverzeichnisse(customerId: number): Observable<TakedaLeistungsverzeichnis[]> {
    return this.http.get<TakedaLeistungsverzeichnis[]>(
      `${environment.apiBaseUrl}/stammkunde/${customerId}/takeda/lv`
    );
  }

  getTakedaAktivesLeistungsverzeichnis(customerId: number): Observable<TakedaLeistungsverzeichnis | null> {
    return this.http.get<TakedaLeistungsverzeichnis | null>(
      `${environment.apiBaseUrl}/stammkunde/${customerId}/takeda/lv/active`
    ).pipe(map(lv => lv ?? null));
  }

  getTakedaLvPositionen(
    customerId: number,
    lvId: number,
    search?: string
  ): Observable<TakedaLvPosition[]> {
    let params = new HttpParams();

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<TakedaLvPosition[]>(
      `${environment.apiBaseUrl}/stammkunde/${customerId}/takeda/lv/${lvId}/positions`,
      { params }
    );
  }

  importTakedaLeistungsverzeichnis(
    customerId: number,
    file: File,
    name?: string
  ): Observable<TakedaLeistungsverzeichnis> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    if (name?.trim()) {
      formData.append('name', name.trim());
    }

    return this.http.post<TakedaLeistungsverzeichnis>(
      `${environment.apiBaseUrl}/stammkunde/${customerId}/takeda/lv/import`,
      formData
    );
  }

  addTakedaLvPosition(
    customerId: number,
    lvId: number,
    position: TakedaLvPositionSave
  ): Observable<TakedaLvPosition> {
    return this.http.post<TakedaLvPosition>(
      `${environment.apiBaseUrl}/stammkunde/${customerId}/takeda/lv/${lvId}/positions`,
      position
    );
  }

  updateTakedaLvPosition(
    customerId: number,
    lvId: number,
    positionId: number,
    position: TakedaLvPositionSave
  ): Observable<TakedaLvPosition> {
    return this.http.put<TakedaLvPosition>(
      `${environment.apiBaseUrl}/stammkunde/${customerId}/takeda/lv/${lvId}/positions/${positionId}`,
      position
    );
  }

  deleteTakedaLvPosition(
    customerId: number,
    lvId: number,
    positionId: number
  ): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiBaseUrl}/stammkunde/${customerId}/takeda/lv/${lvId}/positions/${positionId}`
    );
  }

  /**
   * Bereits verwendete Einsatzorte ("Bauten") dieses Stammkunden als
   * Vorschlagsliste. Es gibt keine eigene Bau-Entity – die Orte kommen aus
   * Auftraege.Location und sind auf diesen Kunden beschränkt.
   */
  getCustomerLocations(customerId: number): Observable<string[]> {
    return this.http.get<string[]>(`${this.auftragUrl}/customer/${customerId}/locations`);
  }

  /**
   * Aggregierte Stunden- und Materialdaten eines Takeda-Auftrags für die
   * Projektansicht. Reiner Lesepfad; die Antwort enthält bewusst keine Preise.
   * Die Sichtbarkeit entspricht den bestehenden Einsatz-/Stunden-Endpunkten.
   */
  getTakedaProjektDaten(auftragId: number): Observable<TakedaProjektDaten> {
    return this.http.get<TakedaProjektDaten>(
      `${this.auftragUrl}/${auftragId}/takeda/project-data`);
  }

  /**
   * Bepreisungsdaten eines Takeda-Auftrags. Eigener, geschützter Pfad – nur
   * Bauleiter dieses Stammkunden, Admin und SuperAdmin erhalten hier Preise.
   * Die normale Projektansicht (`getTakedaProjektDaten`) bleibt preisfrei.
   */
  getTakedaPricing(auftragId: number): Observable<TakedaPricing> {
    return this.http.get<TakedaPricing>(
      `${this.auftragUrl}/${auftragId}/takeda/pricing`);
  }

  // ── Takeda-Abrechnungsexport (ab "Bepreist", nur Bauleiter/Admin) ──

  /**
   * Fehlende Stundensatz-Schnappschüsse eines Altauftrags nachtragen.
   * Schreibt nur dort, wo noch keiner existiert – bestehende bleiben unverändert.
   */
  nachtragenTakedaStundensaetze(auftragId: number): Observable<{ aktualisiert: number; message: string }> {
    return this.http.post<{ aktualisiert: number; message: string }>(
      `${this.auftragUrl}/${auftragId}/takeda/stundensaetze-nachtragen`, {});
  }

  /** Fertig gerechnete Abrechnungsgrundlage – Basis für das PDF. */
  getTakedaAbrechnung(auftragId: number): Observable<TakedaAbrechnung> {
    return this.http.get<TakedaAbrechnung>(
      `${this.auftragUrl}/${auftragId}/takeda/export/data`);
  }

  /** Nur Gesamtübersicht: eine Mappe mit ausschließlich dem Blatt „Übersicht“. */
  downloadTakedaExcelUebersicht(auftragId: number): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.auftragUrl}/${auftragId}/takeda/export/excel/uebersicht`,
      { responseType: 'blob', observe: 'response' });
  }

  /**
   * Gesamtübersicht und einzelne Einsätze: Blatt „Übersicht“ plus ein Regieblatt
   * je Einsatz. Bewusst über HttpClient und nicht per window.open(): nur so
   * läuft der Aufruf durch den AuthInterceptor und trägt den Bearer-Token.
   */
  downloadTakedaExcel(auftragId: number): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.auftragUrl}/${auftragId}/takeda/export/excel`,
      { responseType: 'blob', observe: 'response' });
  }

  // ── Excel-Export „Nach Einsätzen“ (nur Takeda) ──

  /** Einsätze des Projekts für die Auswahl im Exportdialog – aus der Abrechnung abgeleitet. */
  getTakedaExportEinsaetze(auftragId: number): Observable<TakedaExportEinsatzOption[]> {
    return this.http.get<TakedaExportEinsatzOption[]>(
      `${this.auftragUrl}/${auftragId}/takeda/export/einsaetze`);
  }

  /** Eine Arbeitsmappe mit einem Tabellenblatt je Einsatz (RA0001, RA0002, …). */
  downloadTakedaExcelAlleEinsaetze(auftragId: number): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.auftragUrl}/${auftragId}/takeda/export/excel/einsaetze`,
      { responseType: 'blob', observe: 'response' });
  }

  /** Genau ein Tabellenblatt mit den Daten eines Einsatzes; der Server prüft die Zugehörigkeit zum Projekt. */
  downloadTakedaExcelEinsatz(auftragId: number, einsatzId: number): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.auftragUrl}/${auftragId}/takeda/export/excel/einsaetze/${einsatzId}`,
      { responseType: 'blob', observe: 'response' });
  }

  // ── Materialkorrektur durch den Bauleiter (nur Recorded/Approved/Completed) ──

  /** LV-Suche für die Korrektur in der Projektansicht; Antwort ohne Preisfelder. */
  searchTakedaProjektLvPositionen(
    auftragId: number,
    search: string
  ): Observable<TakedaTagesberichtLvPosition[]> {
    const params = new HttpParams().set('search', search);
    return this.http.get<TakedaTagesberichtLvPosition[]>(
      `${this.auftragUrl}/${auftragId}/takeda/lv-positionen`, { params });
  }

  setTakedaLvPositionMenge(auftragId: number, lvPositionId: number, menge: number): Observable<void> {
    return this.http.put<void>(
      `${this.auftragUrl}/${auftragId}/takeda/lv-position/${lvPositionId}/menge`, { menge });
  }

  deleteTakedaProjektLvPosition(auftragId: number, lvPositionId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.auftragUrl}/${auftragId}/takeda/lv-position/${lvPositionId}`);
  }

  /** Den Snapshot baut der Server – gesendet werden nur Referenz und Menge. */
  addTakedaProjektLvPosition(
    auftragId: number,
    arbeitsbereichId: number,
    lvPositionId: number,
    menge: number
  ): Observable<void> {
    return this.http.post<void>(
      `${this.auftragUrl}/${auftragId}/takeda/arbeitsbereich/${arbeitsbereichId}/lv-position`,
      { lvPositionId, menge });
  }

  updateTakedaFreiesMaterial(
    auftragId: number,
    materialId: number,
    material: { bezeichnung: string; menge: number; einheit?: string | null }
  ): Observable<void> {
    return this.http.put<void>(
      `${this.auftragUrl}/${auftragId}/takeda/free-material/${materialId}`, material);
  }

  deleteTakedaFreiesMaterial(auftragId: number, materialId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.auftragUrl}/${auftragId}/takeda/free-material/${materialId}`);
  }

  addTakedaFreiesMaterial(
    auftragId: number,
    arbeitsbereichId: number,
    material: { bezeichnung: string; menge: number; einheit?: string | null }
  ): Observable<void> {
    return this.http.post<void>(
      `${this.auftragUrl}/${auftragId}/takeda/arbeitsbereich/${arbeitsbereichId}/free-material`,
      material);
  }

  /** Preis pro Einheit einer freien Materialposition setzen (nur Completed/Priced). */
  setTakedaFreiesMaterialPreis(
    auftragId: number,
    materialId: number,
    price: number | null
  ): Observable<void> {
    return this.http.put<void>(
      `${this.auftragUrl}/${auftragId}/takeda/free-material/${materialId}/price`,
      { price });
  }

  // ── Altmaterial (ResourceAssignmentMaterials) ──
  //
  // Diese Zeilen stammen aus Aufträgen, die vor der Takeda-Erweiterung erfasst
  // wurden. Der erste Aufruf übernimmt sie serverseitig in die Takeda-Struktur;
  // die ursprüngliche Zeile bleibt als historischer Bestand erhalten. Wiederholte
  // Aufrufe treffen dieselbe Korrekturzeile und erzeugen keine Duplikate.

  /** Altmaterial korrigieren (Bezeichnung/Menge/Einheit); der Preis bleibt unberührt. */
  updateTakedaLegacyMaterial(
    auftragId: number,
    legacyMaterialId: number,
    material: { bezeichnung: string; menge: number; einheit?: string | null }
  ): Observable<void> {
    return this.http.put<void>(
      `${this.auftragUrl}/${auftragId}/takeda/legacy-material/${legacyMaterialId}`, material);
  }

  /** Altmaterial bepreisen (nur Completed/Priced). */
  setTakedaLegacyMaterialPreis(
    auftragId: number,
    legacyMaterialId: number,
    price: number | null
  ): Observable<void> {
    return this.http.put<void>(
      `${this.auftragUrl}/${auftragId}/takeda/legacy-material/${legacyMaterialId}/price`,
      { price });
  }

  /**
   * Freies Material zu einem Einsatz hinzufügen, der noch keinen
   * Takeda-Arbeitsbereich hat. Der Server legt den Container dabei an.
   */
  addTakedaEinsatzFreiesMaterial(
    auftragId: number,
    assignmentId: number,
    material: { bezeichnung: string; menge: number; einheit?: string | null }
  ): Observable<void> {
    return this.http.post<void>(
      `${this.auftragUrl}/${auftragId}/takeda/einsatz/${assignmentId}/free-material`,
      material);
  }

  /** LV-Position zu einem Einsatz ohne Takeda-Arbeitsbereich hinzufügen. */
  addTakedaEinsatzLvPosition(
    auftragId: number,
    assignmentId: number,
    lvPositionId: number,
    menge: number
  ): Observable<void> {
    return this.http.post<void>(
      `${this.auftragUrl}/${auftragId}/takeda/einsatz/${assignmentId}/lv-position`,
      { lvPositionId, menge });
  }

  // ── Takeda-Tagesbericht (nur für Takeda-Einsätze) ──

  /**
   * Tagesbericht eines Einsatzes für einen Tag (workDate = yyyy-MM-dd).
   * Liefert null, wenn für diesen Tag noch kein Bericht existiert (HTTP 204).
   */
  getTakedaTagesbericht(auftragId: number, assignmentId: number, workDate: string): Observable<TakedaTagesbericht | null> {
    const params = new HttpParams().set('workDate', workDate);
    return this.http.get<TakedaTagesbericht | null>(this.tagesberichtUrl(auftragId, assignmentId), { params })
      .pipe(map(report => report ?? null));
  }

  /** Entwurf anlegen bzw. vollständig ersetzen (unvollständige Angaben erlaubt). */
  saveTakedaTagesbericht(auftragId: number, assignmentId: number, report: TakedaTagesberichtSave): Observable<TakedaTagesbericht> {
    return this.http.put<TakedaTagesbericht>(this.tagesberichtUrl(auftragId, assignmentId), report);
  }

  /** Gespeicherten Entwurf abschließen – erzeugt serverseitig die normalen Stunden-Einträge. */
  submitTakedaTagesbericht(auftragId: number, assignmentId: number, workDate: string): Observable<TakedaTagesbericht> {
    const params = new HttpParams().set('workDate', workDate);
    return this.http.post<TakedaTagesbericht>(`${this.tagesberichtUrl(auftragId, assignmentId)}/submit`, {}, { params });
  }

  /**
   * LV-Suche für die Erfassung im Tagesbericht.
   *
   * Eigener Endpunkt, dessen Antwort KEINE Preisfelder enthält – im Gegensatz zur
   * LV-Verwaltung unter Wartung → Kunden. Gesucht wird nur im aktiven LV des
   * Kunden und nur nach echten Positionen (Typ "P"); der Server begrenzt auf 25
   * Treffer und sucht erst ab 2 Zeichen.
   */
  searchTakedaTagesberichtLvPositionen(
    auftragId: number,
    assignmentId: number,
    search: string
  ): Observable<TakedaTagesberichtLvPosition[]> {
    const params = new HttpParams().set('search', search);
    return this.http.get<TakedaTagesberichtLvPosition[]>(
      `${this.tagesberichtUrl(auftragId, assignmentId)}/lv-positionen`,
      { params }
    );
  }

  private tagesberichtUrl(auftragId: number, assignmentId: number): string {
    return `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/tagesbericht`;
  }

  // ── Material-Freigabe ──

  approveMaterial(auftragId: number, materialId: number): Observable<EinsatzMaterial> {
    return this.http.post<EinsatzMaterial>(`${this.auftragUrl}/${auftragId}/materials/${materialId}/approve`, {});
  }

  reopenMaterial(auftragId: number, materialId: number): Observable<EinsatzMaterial> {
    return this.http.post<EinsatzMaterial>(`${this.auftragUrl}/${auftragId}/materials/${materialId}/reopen`, {});
  }

  /** Alle offenen Materialposten eines Auftrags auf einmal freigeben. */
  approveAllMaterials(auftragId: number): Observable<{ approved: number }> {
    return this.http.post<{ approved: number }>(`${this.auftragUrl}/${auftragId}/materials/approve-all`, {});
  }

  // ── Bauleiter-Gesamtübersicht ──

  getOverview(customerId?: number, from?: string, to?: string): Observable<AuftragOverview[]> {
    const params: string[] = [];
    if (customerId != null) { params.push(`customerId=${customerId}`); }
    if (from) { params.push(`from=${from}`); }
    if (to) { params.push(`to=${to}`); }
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<AuftragOverview[]>(`${this.auftragUrl}/overview${query}`);
  }

  getHoursByPerson(customerId?: number, from?: string, to?: string): Observable<PersonHours[]> {
    const params: string[] = [];
    if (customerId != null) { params.push(`customerId=${customerId}`); }
    if (from) { params.push(`from=${from}`); }
    if (to) { params.push(`to=${to}`); }
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<PersonHours[]>(`${this.auftragUrl}/overview/by-person${query}`);
  }

  /** Person per Drag & Drop einem Einsatz zuteilen. */
  addParticipant(auftragId: number, assignmentId: number, userId: string): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/participants/${userId}`, {});
  }

  /** Person (Teilnehmer) von einem Einsatz entfernen. */
  removeParticipant(auftragId: number, assignmentId: number, userId: string): Observable<AuftragAssignment> {
    return this.http.delete<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/participants/${userId}`);
  }

  /** Arbeiter (ohne Login) per Drag & Drop einem Einsatz zuteilen. */
  addWorkerToAssignment(auftragId: number, assignmentId: number, workerId: number): Observable<AuftragAssignment> {
    return this.http.post<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/workers/${workerId}`, {});
  }

  /** Arbeiter (ohne Login) von einem Einsatz entfernen. */
  removeWorkerFromAssignment(auftragId: number, assignmentId: number, workerId: number): Observable<AuftragAssignment> {
    return this.http.delete<AuftragAssignment>(
      `${this.auftragUrl}/${auftragId}/assignments/${assignmentId}/workers/${workerId}`);
  }

  // ── Arbeiter (ohne Login) im Team ──

  getWorkers(customerId: number): Observable<StammkundeWorker[]> {
    return this.http.get<StammkundeWorker[]>(`${environment.apiBaseUrl}/stammkunde/${customerId}/workers`);
  }

  addWorker(customerId: number, worker: { name: string; phone?: string }): Observable<StammkundeWorker> {
    return this.http.post<StammkundeWorker>(`${environment.apiBaseUrl}/stammkunde/${customerId}/workers`, worker);
  }

  updateWorker(customerId: number, workerId: number, worker: { name: string; phone?: string }): Observable<StammkundeWorker> {
    return this.http.put<StammkundeWorker>(
      `${environment.apiBaseUrl}/stammkunde/${customerId}/workers/${workerId}`, worker);
  }

  deleteWorker(customerId: number, workerId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiBaseUrl}/stammkunde/${customerId}/workers/${workerId}`);
  }

  // ── Team (Mitgliedschaften) ──

  getMembers(customerId: number): Observable<StammkundeMember[]> {
    return this.http.get<StammkundeMember[]>(`${this.accessUrl}/stammkunde/${customerId}/members`);
  }

  setMembers(customerId: number, members: StammkundeMember[]): Observable<void> {
    return this.http.put<void>(`${this.accessUrl}/stammkunde/${customerId}/members`, members);
  }

  // ── Lager-Verknüpfung ──

  getLinkedWarehouses(customerId: number): Observable<WarehouseRef[]> {
    return this.http.get<WarehouseRef[]>(`${this.accessUrl}/stammkunde/${customerId}/warehouses`);
  }

  linkWarehouse(customerId: number, warehouseId: number): Observable<void> {
    return this.http.post<void>(`${this.accessUrl}/stammkunde/${customerId}/warehouses/${warehouseId}`, {});
  }

  unlinkWarehouse(customerId: number, warehouseId: number): Observable<void> {
    return this.http.delete<void>(`${this.accessUrl}/stammkunde/${customerId}/warehouses/${warehouseId}`);
  }

  getMyWarehouses(): Observable<WarehouseRef[]> {
    return this.http.get<WarehouseRef[]>(`${this.accessUrl}/my-warehouses`);
  }
}
